import {useState, type ReactNode} from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import {useTheme} from '@mui/material/styles'
import {
  CalendarDays,
  ClipboardList,
  Eye,
  FileText,
  Layers,
  Paperclip,
  RefreshCw,
  X,
} from 'lucide-react'
import {formatTopicRequestAuthor, formatTopicRequestLabel} from '../data/requestIdentity'
import {parseTopicNumber, type TopicFormDefinition} from '../data/topicForms'
import type {TopicRequest} from '../services/topicsApi'
import TopicAttachmentList from './TopicAttachmentList'
import {PromotionSummary} from './TopicPromotionPicker'
import {TopicReferenceSummary} from './TopicReferencePicker'

type Props = {
  topicId: string
  definition: TopicFormDefinition
  requests: TopicRequest[]
  loading: boolean
  error: string
  onRefresh: () => void
}

export function formatTopicDate(value: string) {
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('pt-BR')
}

export {formatAttachmentSize as formatFileSize} from '../data/attachmentPreview'

function DetailSection({
  title,
  icon,
  count,
  children,
}: {
  title: string
  icon: ReactNode
  count?: number
  children: ReactNode
}) {
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: {xs: 2, sm: 2.5},
          py: 1.75,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{display: 'flex', color: 'primary.main'}} aria-hidden='true'>
          {icon}
        </Box>
        <Typography component='h3' variant='subtitle2' fontWeight={600} sx={{flex: 1}}>
          {title}
        </Typography>
        {count !== undefined && (
          <Chip size='small' label={count} variant='outlined' sx={{height: 23, minWidth: 28}} />
        )}
      </Box>
      <Box sx={{p: {xs: 2, sm: 2.5}}}>{children}</Box>
    </Box>
  )
}

function RequestCounts({request}: {request: TopicRequest}) {
  return (
    <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
      <Chip
        size='small'
        variant='outlined'
        icon={<Layers size={14} />}
        label={
          request.promotions.length + (request.promotions.length === 1 ? ' promoção' : ' promoções')
        }
      />
      {request.families?.length ? (
        <Chip
          size='small'
          variant='outlined'
          icon={<Layers size={14} />}
          label={
            request.families.length + (request.families.length === 1 ? ' família' : ' famílias')
          }
        />
      ) : null}
      {request.segmentation && (
        <Chip size='small' variant='outlined' icon={<Layers size={14} />} label='1 segmentação' />
      )}
      <Chip
        size='small'
        variant='outlined'
        icon={<Paperclip size={14} />}
        label={
          request.attachments.length + (request.attachments.length === 1 ? ' anexo' : ' anexos')
        }
      />
    </Box>
  )
}

export default function TopicRequestHistory({
  topicId,
  definition,
  requests,
  loading,
  error,
  onRefresh,
}: Props) {
  const theme = useTheme()
  const showTable = useMediaQuery(theme.breakpoints.up('md'))
  const [selected, setSelected] = useState<TopicRequest | null>(null)
  const openRequest = (request: TopicRequest) => {
    setSelected(request)
  }

  const fieldDisplayValue = (key: string) => {
    const field = definition.fields.find((item) => item.key === key)
    const raw = selected?.values[key]
    const label = (value: unknown) =>
      field?.options?.find((item) => item.value === String(value))?.label || String(value ?? '')
    if (field?.type === 'money' && raw !== undefined && raw !== null && raw !== '') {
      const value =
        typeof raw === 'string' && /^\d+(?:\.\d+)?$/.test(raw)
          ? Number(raw)
          : parseTopicNumber(raw, true)
      if (value !== undefined && Number.isFinite(value)) {
        return value.toLocaleString('pt-BR', {
          style: 'currency',
          currency: 'BRL',
          maximumFractionDigits: 6,
        })
      }
    }
    if (field?.type === 'datetime' && typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(raw))
      return formatTopicDate(raw)
    return Array.isArray(raw) ? raw.map(label).join(', ') : label(raw)
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: {xs: 'flex-start', sm: 'center'},
          justifyContent: 'space-between',
          gap: 2,
          mb: 3,
        }}
      >
        <Box sx={{minWidth: 0}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 0.5}}>
            <Typography component='h2' variant='subtitle1' fontWeight={600}>
              Solicitações salvas
            </Typography>
            {!loading && (
              <Chip
                size='small'
                label={requests.length}
                sx={{height: 23, bgcolor: 'grey.100', fontWeight: 600}}
              />
            )}
          </Box>
          <Typography variant='body2' color='text.secondary'>
            Consulte os registros e os arquivos enviados neste tópico.
          </Typography>
        </Box>
        <Button
          variant='outlined'
          size='small'
          startIcon={<RefreshCw size={16} />}
          onClick={onRefresh}
          disabled={loading}
          sx={{flexShrink: 0, minHeight: 36}}
        >
          Atualizar
        </Button>
      </Box>
      {error && (
        <Alert severity='error' sx={{mb: 2}}>
          {error}
        </Alert>
      )}
      {loading ? (
        <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 7}}>
          <CircularProgress size={28} aria-label='Carregando solicitações' />
          <Typography variant='body2' color='text.secondary'>
            Carregando solicitações...
          </Typography>
        </Box>
      ) : !requests.length && !error ? (
        <Box
          sx={{
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 2,
            py: 6,
            px: 3,
            textAlign: 'center',
            bgcolor: 'grey.50',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 48,
              height: 48,
              mx: 'auto',
              mb: 2,
              borderRadius: 2,
              bgcolor: 'background.paper',
              color: 'text.secondary',
            }}
          >
            <ClipboardList size={24} aria-hidden='true' />
          </Box>
          <Typography variant='subtitle2' fontWeight={600} sx={{overflowWrap: 'anywhere'}}>
            Nenhuma solicitação salva para este tópico.
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{mt: 0.75}}>
            As solicitações aparecerão aqui após o primeiro salvamento.
          </Typography>
        </Box>
      ) : requests.length > 0 ? (
        <>
          {showTable ? (
            <TableContainer sx={{border: '1px solid', borderColor: 'divider', borderRadius: 2}}>
              <Table aria-label='Solicitações salvas' sx={{minWidth: 660}}>
                <TableHead sx={{bgcolor: 'grey.50'}}>
                  <TableRow
                    sx={{
                      '& th': {color: 'text.secondary', fontWeight: 600, fontSize: 12, py: 1.75},
                    }}
                  >
                    <TableCell>Solicitação</TableCell>
                    <TableCell>Data do registro</TableCell>
                    <TableCell align='center'>Promoções</TableCell>
                    <TableCell align='center'>Anexos</TableCell>
                    <TableCell align='right'>Detalhes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {requests.map((request) => (
                    <TableRow
                      key={request.id}
                      hover
                      sx={{'&:last-child td': {borderBottom: 0}, '& td': {py: 2}}}
                    >
                      <TableCell>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.25}}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 34,
                              height: 34,
                              borderRadius: 1.5,
                              bgcolor: 'grey.100',
                              color: 'text.secondary',
                              flexShrink: 0,
                            }}
                          >
                            <FileText size={17} aria-hidden='true' />
                          </Box>
                          <Box sx={{minWidth: 0}}>
                            <Typography
                              variant='body2'
                              fontWeight={600}
                              sx={{overflowWrap: 'anywhere'}}
                            >
                              {formatTopicRequestLabel(request)}
                            </Typography>
                            <Typography
                              variant='caption'
                              color='text.secondary'
                              sx={{display: 'block', mt: 0.5, overflowWrap: 'anywhere'}}
                            >
                              Realizada por {formatTopicRequestAuthor(request)}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={{whiteSpace: 'nowrap'}}>
                        <Typography variant='body2'>
                          {formatTopicDate(request.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell align='center'>
                        <Chip
                          size='small'
                          variant='outlined'
                          label={request.promotions.length || '—'}
                          sx={{minWidth: 34}}
                        />
                      </TableCell>
                      <TableCell align='center'>
                        <Chip
                          size='small'
                          variant='outlined'
                          label={request.attachments.length || '—'}
                          sx={{minWidth: 34}}
                        />
                      </TableCell>
                      <TableCell align='right'>
                        <Button
                          size='small'
                          startIcon={<Eye size={16} />}
                          onClick={() => openRequest(request)}
                          aria-label={'Ver ' + formatTopicRequestLabel(request).toLowerCase()}
                          sx={{whiteSpace: 'nowrap'}}
                        >
                          Ver detalhes
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{display: 'grid', gap: 1.5}}>
              {requests.map((request) => (
                <Box
                  key={request.id}
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                      mb: 1.5,
                    }}
                  >
                    <Box sx={{minWidth: 0, flex: 1}}>
                      <Typography
                        variant='subtitle2'
                        fontWeight={600}
                        sx={{overflowWrap: 'anywhere'}}
                      >
                        {formatTopicRequestLabel(request)}
                      </Typography>
                    </Box>
                    <Button
                      size='small'
                      variant='outlined'
                      startIcon={<Eye size={16} />}
                      onClick={() => openRequest(request)}
                      aria-label={'Ver ' + formatTopicRequestLabel(request).toLowerCase()}
                    >
                      Ver detalhes
                    </Button>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      color: 'text.secondary',
                      mb: 1.5,
                    }}
                  >
                    <CalendarDays size={15} aria-hidden='true' />
                    <Typography variant='body2'>{formatTopicDate(request.createdAt)}</Typography>
                  </Box>
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{mb: 1.5, overflowWrap: 'anywhere'}}
                  >
                    Realizada por {formatTopicRequestAuthor(request)}
                  </Typography>
                  <RequestCounts request={request} />
                </Box>
              ))}
            </Box>
          )}
          <Typography variant='caption' color='text.secondary' sx={{display: 'block', mt: 2}}>
            Mostrando as {requests.length} solicitações mais recentes.
          </Typography>
        </>
      ) : null}
      <Dialog
        open={!!selected}
        onClose={() => setSelected(null)}
        fullWidth
        maxWidth='md'
        aria-labelledby='topic-request-details-title'
        PaperProps={{
          sx: {
            m: {xs: 1.5, sm: 4},
            width: {xs: 'calc(100% - 24px)', sm: 'calc(100% - 64px)'},
            maxHeight: {xs: 'calc(100dvh - 24px)', sm: 'calc(100dvh - 64px)'},
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          component='div'
          sx={{
            px: {xs: 2, sm: 3},
            py: 2.5,
            pr: {xs: 7, sm: 8},
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography
            variant='overline'
            color='text.secondary'
            sx={{lineHeight: 1.5, letterSpacing: 0.6}}
          >
            Detalhes da solicitação
          </Typography>
          <Typography
            component='h2'
            variant='h6'
            fontWeight={600}
            id='topic-request-details-title'
            sx={{mt: 0.25, overflowWrap: 'anywhere'}}
          >
            {formatTopicRequestLabel(selected)}
          </Typography>
          {selected && (
            <Box sx={{mt: 0.75}}>
              <Typography variant='body2' color='text.secondary' sx={{overflowWrap: 'anywhere'}}>
                Realizada por {formatTopicRequestAuthor(selected)}
              </Typography>
              <Typography variant='caption' color='text.secondary' sx={{display: 'block', mt: 0.5}}>
                Realizada em {formatTopicDate(selected.createdAt)}
              </Typography>
            </Box>
          )}
          <IconButton
            aria-label='Fechar detalhes da solicitação'
            onClick={() => setSelected(null)}
            sx={{position: 'absolute', right: {xs: 12, sm: 20}, top: 18}}
          >
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            bgcolor: '#f8fafc',
            p: {xs: 2, sm: 3},
            '&.MuiDialogContent-root': {pt: {xs: 2, sm: 3}},
          }}
        >
          {selected && (
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 2.5}}>
              <RequestCounts request={selected} />
              <DetailSection title='Detalhamento da solicitação' icon={<FileText size={18} />}>
                <Typography
                  variant='body2'
                  color={selected.details ? 'text.primary' : 'text.secondary'}
                  sx={{whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', lineHeight: 1.7}}
                >
                  {selected.details || 'Não informado'}
                </Typography>
              </DetailSection>
              {definition.fields.length > 0 && (
                <DetailSection title='Informações solicitadas' icon={<ClipboardList size={18} />}>
                  <Box
                    component='dl'
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))'},
                      gap: 2,
                      m: 0,
                    }}
                  >
                    {definition.fields.map((field) => {
                      const value = fieldDisplayValue(field.key)
                      return (
                        <Box
                          key={field.key}
                          sx={{
                            minWidth: 0,
                            gridColumn: field.type === 'textarea' ? '1 / -1' : 'auto',
                            p: 1.75,
                            borderRadius: 1.5,
                            bgcolor: 'grey.50',
                          }}
                        >
                          <Typography
                            component='dt'
                            variant='caption'
                            color='text.secondary'
                            sx={{display: 'block', mb: 0.75, lineHeight: 1.5}}
                          >
                            {field.label}
                          </Typography>
                          <Typography
                            component='dd'
                            variant='body2'
                            fontWeight={value ? 500 : 400}
                            color={value ? 'text.primary' : 'text.secondary'}
                            sx={{
                              m: 0,
                              whiteSpace: 'pre-wrap',
                              overflowWrap: 'anywhere',
                              lineHeight: 1.6,
                            }}
                          >
                            {value || 'Não informado'}
                          </Typography>
                        </Box>
                      )
                    })}
                  </Box>
                </DetailSection>
              )}
              {selected.promotions.length > 0 && (
                <DetailSection
                  title='Promoções selecionadas'
                  icon={<Layers size={18} />}
                  count={selected.promotions.length}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: 'minmax(0, 1fr)',
                        sm:
                          selected.promotions.length > 1
                            ? 'repeat(2, minmax(0, 1fr))'
                            : 'minmax(0, 1fr)',
                      },
                      gap: 1.5,
                    }}
                  >
                    {selected.promotions.map((promotion) => (
                      <Box
                        key={promotion.id}
                        sx={{
                          p: 2,
                          bgcolor: 'grey.50',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderLeft: '3px solid',
                          borderLeftColor: 'primary.main',
                          borderRadius: 1.5,
                          minWidth: 0,
                        }}
                      >
                        <PromotionSummary promotion={promotion} />
                      </Box>
                    ))}
                  </Box>
                </DetailSection>
              )}
              {definition.requiresOriginalList && selected.originalList && (
                <DetailSection title='Lista Original' icon={<Layers size={18} />}>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: 'grey.50',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1.5,
                    }}
                  >
                    <PromotionSummary promotion={selected.originalList} />
                  </Box>
                </DetailSection>
              )}
              {definition.hasSegmentationSelector && (
                <DetailSection title='Segmentação selecionada' icon={<Layers size={18} />}>
                  {selected.segmentation ? (
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: 'grey.50',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1.5,
                        minWidth: 0,
                      }}
                    >
                      <TopicReferenceSummary reference={selected.segmentation} />
                    </Box>
                  ) : (
                    <Typography variant='body2' color='text.secondary'>
                      Não informada
                    </Typography>
                  )}
                </DetailSection>
              )}
              {definition.requiresFamilies && (
                <DetailSection
                  title='Famílias selecionadas'
                  icon={<Layers size={18} />}
                  count={selected.families?.length || 0}
                >
                  {selected.families?.length ? (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: 'minmax(0, 1fr)',
                          sm:
                            selected.families.length > 1
                              ? 'repeat(2, minmax(0, 1fr))'
                              : 'minmax(0, 1fr)',
                        },
                        gap: 1.5,
                      }}
                    >
                      {selected.families.map((family) => (
                        <Box
                          key={family.id}
                          sx={{
                            p: 2,
                            bgcolor: 'grey.50',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1.5,
                            minWidth: 0,
                          }}
                        >
                          <TopicReferenceSummary reference={family} />
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant='body2' color='text.secondary'>
                      Nenhuma família selecionada.
                    </Typography>
                  )}
                </DetailSection>
              )}
              <DetailSection
                title='Anexos'
                icon={<Paperclip size={18} />}
                count={selected.attachments.length}
              >
                {selected.attachments.length ? (
                  <TopicAttachmentList
                    attachments={selected.attachments}
                    topicId={topicId}
                    requestId={selected.id}
                  />
                ) : (
                  <Typography variant='body2' color='text.secondary'>
                    Nenhum anexo enviado.
                  </Typography>
                )}
              </DetailSection>
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: {xs: 2, sm: 3},
            py: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant='caption' color='text.secondary' sx={{flex: 1}}>
            Consulta da solicitação salva
          </Typography>
          <Button variant='outlined' onClick={() => setSelected(null)} sx={{minWidth: 100}}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
